import { useCallback, useEffect, useState } from "react";
import { MessageCircle, Trash2, Search, ThumbsUp, ThumbsDown, RefreshCw, Eye, Lightbulb } from "lucide-react";
import Markdown from "react-markdown";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import HelpCostCard from "./HelpCostCard";
import HelpInsightsTabs from "./HelpInsightsTabs";
import WeeklyReportRow from "./WeeklyReportRow";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function HelpSessionsBlock() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (query = "") => {
    try { setData((await api.get("/admin/help/sessions", { params: { q: query, limit: 100 } })).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openSession = async (id) => {
    try { setActive((await api.get(`/admin/help/sessions/${id}`)).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Dit gesprek verwijderen?")) return;
    setBusy(true);
    try { await api.delete(`/admin/help/sessions/${id}`); toast.success("Gesprek verwijderd."); setActive(null); await load(q); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const removeScope = async (scope) => {
    if (!window.confirm(scope === "all" ? "Alle gesprekken verwijderen?" : "Gesprekken ouder dan 90 dagen verwijderen?")) return;
    setBusy(true);
    try { const { data: d } = await api.delete("/admin/help/sessions", { params: { scope } }); toast.success(`${d.deleted} gesprek(ken) verwijderd.`); await load(q); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const btn = "btn-brutal px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50";

  return (
    <section className="bg-card border-2 border-foreground heavy-shadow" data-testid="help-sessions-block">
      <div className="p-5 border-b-2 border-foreground flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight inline-flex items-center gap-2"><MessageCircle className="w-5 h-5" /> Helpchat</h2>
          <p className="text-xs text-muted-foreground mt-1">Gesprekken van de hulpassistent ({data?.model || "Claude Haiku 4.5"}); automatisch opgeruimd na {data?.retention_days || 90} dagen.</p>
        </div>
        {data && (
          <div className="flex gap-4 text-xs" data-testid="help-sessions-stats">
            <span>Gesprekken <b className="font-mono-num">{data.stats.sessions}</b></span>
            <span>Vragen <b className="font-mono-num">{data.stats.questions}</b></span>
            <span className="inline-flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> <b className="font-mono-num">{data.stats.up}</b></span>
            <span className="inline-flex items-center gap-1"><ThumbsDown className="w-3 h-3" /> <b className="font-mono-num">{data.stats.down}</b></span>
            <span className={`inline-flex items-center gap-1 ${data.stats.gaps ? "text-destructive" : ""}`} title="Open kennisgaten" data-testid="help-stats-gaps"><Lightbulb className="w-3 h-3" /> <b className="font-mono-num">{data.stats.gaps ?? 0}</b></span>
          </div>
        )}
      </div>
      <div className="p-5 space-y-3">
        <HelpCostCard refreshKey={data?.stats.questions} />
        <WeeklyReportRow />
        <div className="flex flex-wrap gap-2 items-center">
          <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op e-mail of tekst…" data-testid="help-sessions-search"
              className="w-full border-2 border-foreground bg-background pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </form>
          <button onClick={() => load(q)} className={`${btn} bg-background text-foreground`} data-testid="help-sessions-refresh"><RefreshCw className="w-3.5 h-3.5" /> Ververs</button>
          <button onClick={() => removeScope("expired")} disabled={busy} className={`${btn} bg-background text-foreground`} data-testid="help-sessions-delete-expired"><Trash2 className="w-3.5 h-3.5" /> Ouder dan 90 dagen</button>
          <button onClick={() => removeScope("all")} disabled={busy || !data?.stats.sessions} className={`${btn} bg-destructive text-white`} data-testid="help-sessions-delete-all"><Trash2 className="w-3.5 h-3.5" /> Alles verwijderen</button>
        </div>
        {!data ? <div className="text-sm text-muted-foreground">Laden…</div> : data.sessions.length === 0 ? (
          <div className="text-sm text-muted-foreground" data-testid="help-sessions-empty">Nog geen gesprekken.</div>
        ) : (
          <div className="overflow-x-auto border-2 border-foreground">
            <table className="w-full text-sm" data-testid="help-sessions-table">
              <thead className="bg-foreground text-background">
                <tr>
                  {["Laatste", "Gebruiker", "Eerste vraag", "Vragen", "Feedback", "Pagina's", ""].map((h) => <th key={h} className="text-left px-3 py-2 font-display uppercase text-[10px] tracking-widest">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.sessions.map((s) => (
                  <tr key={s.id} className="border-t border-border even:bg-muted/30" data-testid={`help-session-row-${s.id}`}>
                    <td className="px-3 py-2 font-mono-num text-xs whitespace-nowrap">{fmt(s.last_at)}</td>
                    <td className="px-3 py-2 text-xs"><div className="font-mono-num">{s.email}</div><div className="text-muted-foreground">{s.tier}</div></td>
                    <td className="px-3 py-2 max-w-[320px] truncate" title={s.first_question}>{s.first_question}</td>
                    <td className="px-3 py-2 font-mono-num text-center">{s.message_count}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap"><span className="text-positive">▲{s.feedback_up}</span> <span className="text-destructive ml-1">▼{s.feedback_down}</span></td>
                    <td className="px-3 py-2 text-[11px] text-muted-foreground max-w-[200px] truncate" title={s.pages.join(", ")}>{s.pages.join(", ")}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      <button onClick={() => openSession(s.id)} className="p-1.5 hover:bg-muted" title="Bekijken" data-testid={`help-session-open-${s.id}`}><Eye className="w-4 h-4" /></button>
                      <button onClick={() => remove(s.id)} className="p-1.5 hover:bg-destructive/10 text-destructive" title="Verwijderen" data-testid={`help-session-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <HelpInsightsTabs refreshKey={data?.stats.questions} gapsOpen={data?.stats.gaps || 0} onChanged={() => load(q)} />
      </div>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
        <DialogContent className="max-w-2xl border-2 border-foreground rounded-none heavy-shadow max-h-[85vh] overflow-y-auto" data-testid="help-session-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display font-black uppercase tracking-tight">Gesprek · {active.email}</DialogTitle>
                <DialogDescription>{fmt(active.started_at)} – {fmt(active.last_at)} · {active.message_count} vragen · pagina's: {active.pages.join(", ") || "—"}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                {active.messages.map((m, i) => (
                  <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                    <div className={`max-w-[85%] px-3 py-2 border-2 border-foreground ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-background"}`}>
                      {m.role === "user" ? <p className="whitespace-pre-wrap">{m.text}</p> : <Markdown>{m.text}</Markdown>}
                      <div className="text-[10px] opacity-70 mt-1 flex gap-2 flex-wrap">
                        <span>{fmt(m.at)}</span>{m.page && <span>{m.page}</span>}
                        {m.feedback === "up" && <ThumbsUp className="w-3 h-3" />}{m.feedback === "down" && <ThumbsDown className="w-3 h-3" />}
                        {m.usage && <span className="font-mono-num">{m.usage.input_tokens}↑ {m.usage.output_tokens}↓ tokens</span>}
                        {m.gap && <span className="inline-flex items-center gap-0.5 text-destructive font-bold" data-testid={`help-session-msg-gap-${i}`}><Lightbulb className="w-3 h-3" /> kennisgat{m.gap_resolved_at ? " (afgehandeld)" : ""}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end pt-2">
                <button onClick={() => remove(active.id)} disabled={busy} className={`${btn} bg-destructive text-white`} data-testid="help-session-dialog-delete"><Trash2 className="w-3.5 h-3.5" /> Verwijderen</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
